/**
 * InfinitSales - Widget do site (5o canal), cliente da API do operador.
 *
 * Espelha o messengerService com o que o canal tem de proprio:
 * - N widgets por workspace (accounts) com snippet de uma linha;
 * - nao existe janela de 24 h: "enviar" e persistir, a entrega e diferida
 *   (a bolha nasce `sent` e vira `delivered`/`read` quando o visitante a ve);
 * - so imagem como midia; sem audio nesta fase;
 * - filtro por widget de origem na lista (`widget_id`).
 */
import api from '../global/api';
import requestCache from '../global/requestCache';

const BASE = '/api/widget';
const STATUS_CACHE_KEY = 'widget:status';
const STATUS_TTL_MS = 60_000;

class WidgetService {
  // ============================================
  // STATUS E CONEXOES
  // ============================================

  async getStatus({ force = false } = {}) {
    const entry = await requestCache.cachedGet(
      STATUS_CACHE_KEY,
      async () => {
        try {
          const response = await api.get(`${BASE}/status`);
          return { data: response.data };
        } catch (e) {
          if (e?.response?.status === 404) return { notFound: e };
          throw e;
        }
      },
      { ttl: STATUS_TTL_MS, force },
    );
    if (entry.notFound) throw entry.notFound;
    return entry.data;
  }

  invalidateStatus() {
    requestCache.invalidate(STATUS_CACHE_KEY);
  }

  async listAccounts() {
    const response = await api.get(`${BASE}/accounts`);
    return response.data;
  }

  async getAccount(accountId) {
    const response = await api.get(`${BASE}/accounts/${accountId}`);
    return response.data;
  }

  async createAccount(payload) {
    const response = await api.post(`${BASE}/accounts`, payload);
    this.invalidateStatus();
    return response.data;
  }

  async updateAccount(accountId, payload) {
    const response = await api.put(`${BASE}/accounts/${accountId}`, payload);
    this.invalidateStatus();
    return response.data;
  }

  async rotateKeys(accountId, rotatePublic = false) {
    const response = await api.post(`${BASE}/accounts/${accountId}/rotate`, null, { params: { rotate_public: rotatePublic } });
    this.invalidateStatus();
    return response.data;
  }

  async disconnect(accountId, purge = false) {
    const response = await api.delete(`${BASE}/accounts/${accountId}`, { params: { purge } });
    this.invalidateStatus();
    return response.data;
  }

  // ============================================
  // CONVERSAS
  // ============================================

  async getChats(limit = 50, offset = 0, extra = {}) {
    const params = { limit, offset };
    if (extra?.since) params.since = extra.since;
    if (extra?.unanswered) params.unanswered = true;
    if (extra?.created_from) params.created_from = extra.created_from;
    if (extra?.created_to) params.created_to = extra.created_to;
    if (extra?.member_id) params.member_id = extra.member_id;
    if (extra?.widget_id) params.widget_id = extra.widget_id;
    if (Array.isArray(extra?.label_ids) && extra.label_ids.length) params.label_ids = extra.label_ids;
    if (Array.isArray(extra?.custom_fields) && extra.custom_fields.length) params.cf = extra.custom_fields;
    const response = await api.get(`${BASE}/chats`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  }

  async searchChats(q, searchMessages = false, customFields = null, widgetId = null) {
    const params = { q, search_messages: searchMessages };
    if (Array.isArray(customFields) && customFields.length) params.cf = customFields;
    if (widgetId) params.widget_id = widgetId;
    const response = await api.get(`${BASE}/chats/search`, { params, paramsSerializer: { indexes: null } });
    return response.data;
  }

  async getChat(chatId) {
    const response = await api.get(`${BASE}/chats/${chatId}`);
    return response.data;
  }

  async markAsRead(chatId) {
    const response = await api.post(`${BASE}/chats/${chatId}/read`);
    return response.data;
  }

  async getMessages(chatId, limit = 50, offset = 0, options = {}) {
    const response = await api.get(`${BASE}/chats/${chatId}/messages`, {
      params: { limit, offset },
      signal: options.signal,
    });
    return response.data;
  }

  async sendMessage(chatId, message) {
    const response = await api.post(`${BASE}/chats/${chatId}/messages`, { message });
    return response.data;
  }

  /** Midia pelo chat (o widget nao tem "destinatario" fora da conversa). */
  async sendMedia(chatId, mediaUrl, mediaType = 'image') {
    const response = await api.post(`${BASE}/chats/${chatId}/messages`, {
      media_url: mediaUrl, media_type: mediaType,
    });
    return response.data;
  }

  async sendInteractive(chatId, interactive, text = null) {
    const response = await api.post(`${BASE}/chats/${chatId}/messages`, { message: text, interactive });
    return response.data;
  }

  async endChat(chatId) {
    const response = await api.post(`${BASE}/chats/${chatId}/end`);
    return response.data;
  }

  async reopenChat(chatId) {
    const response = await api.post(`${BASE}/chats/${chatId}/reopen`);
    return response.data;
  }

  async typing(chatId, typing = true) {
    try {
      await api.post(`${BASE}/chats/${chatId}/typing`, null, { params: { typing } });
    } catch { /* best-effort */ }
  }

  // ============================================
  // UTILS
  // ============================================

  getChatDisplayName(chat) {
    if (!chat) return '';
    return chat.contact_name || (chat.contact_wid ? `Visitante #${String(chat.contact_wid).replace(/-/g, '').slice(-4)}` : '');
  }

  getMessagePreview(chat) {
    if (!chat) return '';
    return chat.last_message_text || '';
  }

  /** O widget nao tem janela de 24 h: sempre aberta (excecao legitima). */
  isWindowOpen() {
    return true;
  }

  formatTime(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

const widgetService = new WidgetService();
export default widgetService;
